import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as amplitude from '@amplitude/analytics-browser';

import { Check } from '@unified-checkout/design-system/icons/index';
import { Modal } from '@unified-checkout/design-system';

import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { notify } from '@app/dashboard/utils/notifier';
import {
  ACTIONS,
  API_KEYS_TYPES,
  API_QUERY_KEYS_MAPPER,
  KEYS_OPERATIONS,
  MODAL_ACTIONS_CONFIG,
  REVOKED_KEY_ERROR_STATUS,
} from '@app/dashboard/constants/settings/api-keys';
import {
  SYSTEM_EVENTS_GROUPING_CODES,
  SYSTEM_EVENTS_SRC_FEATURES,
} from '@app/dashboard/constants/tracking_events/system';
import {
  API_KEYS_EVENTS,
  API_KEYS_EVENTS_PARAMS,
} from '@app/dashboard/constants/tracking_events/api-keys';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';

import type {
  ICreatePayload,
  IViewRecreateModalProps,
  TApiKeysCardType,
} from '@app/dashboard/types/innerLayout/settings/api_keys';

import { getCurrentMode } from '@app/dashboard/utils/getCurrentMode';
import { getApiMapper, handleCopyText } from '../../utils';
import { onToasterShowHandler } from '@app/dashboard/utils/tracking/onToasterShowHandler';

import styles from './ViewRecreateModal.module.scss';

const ViewRecreateModal = ({
  isViewRecreateModalOpen,
  setIsViewRecreateModalOpen,
  currentCardTypeAction: { action, type },
  apiKeysData,
}: IViewRecreateModalProps) => {
  const { t } = useTranslation();
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isLive } = useTestLiveModeStore();
  const queryClient = useQueryClient();

  const [isCopied, setIsCopied] = useState(false);

  const isViewAction = useMemo(() => action === ACTIONS.VIEW, [action]);

  const { mutateAsync: mutateRevokeCreatedKey, isPending } = useMutation({
    mutationFn: ({
      type,
      operation,
    }: {
      type: Omit<TApiKeysCardType, 'hmac'>;
      operation: ICreatePayload['operation'];
    }) => {
      const apiMapper = getApiMapper(isLive);
      const apiFn = apiMapper[type as keyof typeof apiMapper];
      return apiFn(operation);
    },
  });

  const showSuccessNotification = () => {
    const message = t('innerLayout.apiKeys.modal.recreateSuccess', {
      keyType: t(`innerLayout.apiKeys.titles.${type}`),
    });

    notify.success(message, {
      onShowHandler: () =>
        onToasterShowHandler({
          type: 'success',
          trackingCode: (SYSTEM_EVENTS_GROUPING_CODES.API_KEYS.RECREATE as any)[
            type?.replace('Key', '')?.toUpperCase() || ''
          ],
          msg: message,
          srcFeature: SYSTEM_EVENTS_SRC_FEATURES.API_KEYS,
        }),
    });
  };

  const invalidateApiQuery = (queryKey: string[]) => {
    queryClient.invalidateQueries({ queryKey });
  };

  const handleRecreate = async () => {
    const keyType = type as keyof typeof getApiMapper;
    const queryKey = API_QUERY_KEYS_MAPPER[keyType];
    const isPublicOrSecretKey = [
      API_KEYS_TYPES.PUBLIC_KEY,
      API_KEYS_TYPES.SECRET_KEY,
    ].includes(keyType);

    try {
      // revoke exising one if it's public or secret key, else create a new one directly for api key.
      await mutateRevokeCreatedKey({
        type: keyType,
        operation: isPublicOrSecretKey
          ? KEYS_OPERATIONS.REVOKE
          : KEYS_OPERATIONS.RENEW,
      });

      // renew only for public and secret keys
      if (isPublicOrSecretKey) {
        await mutateRevokeCreatedKey({
          type: keyType,
          operation: KEYS_OPERATIONS.CREATE,
        });
      }

      setIsViewRecreateModalOpen(false);
      showSuccessNotification();
      invalidateApiQuery(queryKey);
      amplitude.track(API_KEYS_EVENTS.RECREATE_API_KEYS, {
        [API_KEYS_EVENTS_PARAMS.KEY_TYPE]: keyType,
        [SHARED_EVENTS_PARAMS.ENVIRONMENT]: getCurrentMode(isLive),
        [SHARED_EVENTS_PARAMS.RESULT]: SHARED_EVENTS_PARAMS.SUCCESS,
      });
    } catch (error: unknown) {
      const axiosErr = error as AxiosError<{ message?: string }>;

      if (axiosErr.status === REVOKED_KEY_ERROR_STATUS) {
        //incase the key was already revoked, then we just need to create a new one without trying to revoke again
        if (isPublicOrSecretKey) {
          await mutateRevokeCreatedKey({
            type: keyType,
            operation: KEYS_OPERATIONS.CREATE,
          });
        }
        setIsViewRecreateModalOpen(false);
        showSuccessNotification();
        invalidateApiQuery(queryKey);
        amplitude.track(API_KEYS_EVENTS.RECREATE_API_KEYS, {
          [API_KEYS_EVENTS_PARAMS.KEY_TYPE]: keyType,
          [SHARED_EVENTS_PARAMS.ENVIRONMENT]: getCurrentMode(isLive),
          [SHARED_EVENTS_PARAMS.RESULT]: SHARED_EVENTS_PARAMS.SUCCESS,
        });
        return;
      }

      const message =
        axiosErr.response?.data?.message ||
        axiosErr.message ||
        t('errors.somethingWentWrong');

      notify.error(message, {
        timeout: 3,
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'error',
            trackingCode: (SYSTEM_EVENTS_GROUPING_CODES.API_KEYS as any)[
              type?.replace('Key', '')?.toUpperCase() || ''
            ],
            msg: message,
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.API_KEYS,
          }),
      });

      amplitude.track(API_KEYS_EVENTS.RECREATE_API_KEYS, {
        [API_KEYS_EVENTS_PARAMS.KEY_TYPE]: keyType,
        [SHARED_EVENTS_PARAMS.ENVIRONMENT]: getCurrentMode(isLive),
        [SHARED_EVENTS_PARAMS.RESULT]: SHARED_EVENTS_PARAMS.FAILED,
      });
    }
  };

  const handleCopyBtnClick = () => {
    if (!type) return;
    handleCopyText({
      textToCopy: apiKeysData[type],
      copyTimeoutRef,
      setIsCopied,
    });
  };

  const handleBtnClick = () => {
    if (!action || !type) return;

    const actionMapper = {
      [ACTIONS.VIEW]: handleCopyBtnClick,
      [ACTIONS.RECREATE]: handleRecreate,
    };

    const handler = actionMapper[action];
    if (handler) handler();
  };

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  if (!action || !type) return null;

  const config = MODAL_ACTIONS_CONFIG[action];

  return (
    <>

      <Modal
        open={isViewRecreateModalOpen}
        wrapperClassName={styles['view-recreate-modal']}
        closeOnClickOutside
        isOverlayAboveModal
        onCloseHandler={() => setIsViewRecreateModalOpen(false)}
        hasCloseIcon
        title={t(`innerLayout.apiKeys.modal.${action}`, {
          keyType: t(`innerLayout.apiKeys.titles.${type}`),
        })}
        supportingText={config.getSupportingText(type, apiKeysData, t)}
        btnsProps={[
          {
            color: 'primary',
            text:
              isViewAction && isCopied
                ? t('innerLayout.apiKeys.actions.copied')
                : config.getPrimaryBtnText(t),
            variant: 'contained',
            leadingIcon: isViewAction && isCopied ? Check : config.getIcon(),
            onClickHandler: handleBtnClick,
            disabled: (isViewAction && isCopied) || isPending,
            loading: isPending,
          },
          {
            color: 'secondary',
            variant: 'outlined',
            text: t('innerLayout.apiKeys.actions.cancel'),
            onClickHandler: () => setIsViewRecreateModalOpen(false),
            disabled: isPending,
          },
        ]}
      />
    </>
  );
};

export default ViewRecreateModal;
